/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * The signing algorithm of the key.
 */
export const ExternalKeySummaryAlgorithm = {
  Rs256: "RS256",
  Es256: "ES256",
} as const;
/**
 * The signing algorithm of the key.
 */
export type ExternalKeySummaryAlgorithm = ClosedEnum<
  typeof ExternalKeySummaryAlgorithm
>;

/**
 * The cloud KMS provider of the key.
 */
export const ExternalKeySummaryProvider = {
  AwsKms: "aws_kms",
  GcpKms: "gcp_kms",
} as const;
/**
 * The cloud KMS provider of the key.
 */
export type ExternalKeySummaryProvider = ClosedEnum<
  typeof ExternalKeySummaryProvider
>;

/**
 * Provider-independent summary of an external key.
 */
export type ExternalKeySummary = {
  /**
   * The signing algorithm of the key.
   */
  algorithm: ExternalKeySummaryAlgorithm;
  /**
   * When the key was created.
   */
  createdAt: Date;
  /**
   * The Speakeasy identity (GCP service-account email or AWS principal ARN) the customer granted on the key for the key-policy / IAM-grant model. Not a secret.
   */
  customerGrantReference?: string | undefined;
  /**
   * The external credential Speakeasy uses to authenticate to the key.
   */
  externalCredentialId: string;
  /**
   * The ID of the external key.
   */
  id: string;
  /**
   * A human-readable name for the key.
   */
  name: string;
  /**
   * The organization that owns the key.
   */
  organizationId: string;
  /**
   * The cloud KMS provider of the key.
   */
  provider: ExternalKeySummaryProvider;
  /**
   * When the key was last updated.
   */
  updatedAt: Date;
};

/** @internal */
export const ExternalKeySummaryAlgorithm$inboundSchema: z.ZodMiniEnum<
  typeof ExternalKeySummaryAlgorithm
> = z.enum(ExternalKeySummaryAlgorithm);

/** @internal */
export const ExternalKeySummaryProvider$inboundSchema: z.ZodMiniEnum<
  typeof ExternalKeySummaryProvider
> = z.enum(ExternalKeySummaryProvider);

/** @internal */
export const ExternalKeySummary$inboundSchema: z.ZodMiniType<
  ExternalKeySummary,
  unknown
> = z.pipe(
  z.object({
    algorithm: ExternalKeySummaryAlgorithm$inboundSchema,
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    customer_grant_reference: z.optional(z.string()),
    external_credential_id: z.string(),
    id: z.string(),
    name: z.string(),
    organization_id: z.string(),
    provider: ExternalKeySummaryProvider$inboundSchema,
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
  }),
  z.transform((v) => {
    return remap$(v, {
      "created_at": "createdAt",
      "customer_grant_reference": "customerGrantReference",
      "external_credential_id": "externalCredentialId",
      "organization_id": "organizationId",
      "updated_at": "updatedAt",
    });
  }),
);

export function externalKeySummaryFromJSON(
  jsonString: string,
): SafeParseResult<ExternalKeySummary, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ExternalKeySummary$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ExternalKeySummary' from JSON`,
  );
}
